import { useDropzone, useFileUpload } from "@msuite/ui/file-upload";
import { Select } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { type AsyncModalController, useAsyncModal } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import {
	type ImportColumnsFormSchema,
	importColumnsFormSchema,
	type UploadResultsSchema,
} from "@msuite/utils/schemas";
import type { Nullable } from "@msuite/utils/types";
import { Fragment } from "react";
import { type UseFormReturn, useForm, useWatch } from "react-hook-form";
import { useUpload } from "@/api/files";
import { client } from "@/hono";

const accept = {
	"text/csv": [".csv"],
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
	"application/vnd.ms-excel": [".xls"],
};

export function useOrderFileImport(
	onImport: (
		articles: Array<{ articleId: string; quantity: number }>,
		fileId: Nullable<string>,
	) => void,
) {
	/** Hooks */
	const columnFormMethods = useForm({
		resolver: resolver(importColumnsFormSchema),
	});
	const columnFormController = useAsyncModal<ImportColumnsFormSchema>();
	const importErrorController = useAsyncModal();
	const { t } = useTranslation();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({ uploadFn });
	const dropzone = useDropzone({
		accept,
		onDrop: handleOnFileImport,
		onDropRejected: () => {
			toast.error(t("general.something_went_wrong"));
		},
		multiple: false,
	});

	async function handleOnProcessImport({
		uploadResults,
		query,
		fileId,
	}: UploadResultsSchema & { query?: ImportColumnsFormSchema; fileId?: string }) {
		const response = await client.stock.orders["file-import"].$post({
			json: { uploadResults, fileId },
			query,
		});
		const data = await responseHandler(response);
		if (!data.hasFields && "columns" in data) {
			columnFormMethods.setValue("columnNames", data.columns);
			columnFormMethods.setValue("identifierColumnName", data.identifierField);
			columnFormMethods.setValue("quantityColumnName", data.quantityField);
			const query = await columnFormController.confirm();
			if (typeof query === "boolean") return;
			delete query.columnNames;
			return await handleOnProcessImport({ uploadResults, query, fileId: data.fileId });
		} else if (data.hasFields && "resolvedArticles" in data) {
			if (data.unresolvedArticleNumbers) {
				importErrorController.confirm(undefined, {
					render: (
						<Fragment>
							<p>{t("stock.these_article_numbers_couldnt_be_resolved")}</p>
							<ul>
								{data.unresolvedArticleNumbers.filter(Boolean).map((number, index) => (
									<li key={number}>
										{index + 1}. {number}
									</li>
								))}
							</ul>
						</Fragment>
					),
				});
			}
			onImport(data.resolvedArticles, data.fileId);
		} else {
			toast.error(t("general.something_went_wrong"));
		}
	}

	/** Functions */
	async function handleOnFileImport(acceptedFiles: File[]) {
		toast.promise(
			async () => {
				const uploadResults = await controller.handleOnFileUpload(acceptedFiles);
				controller.handleOnReset();
				await handleOnProcessImport({ uploadResults });
			},
			{
				loading: t("files.processing_file"),
				error: t("files.failed_processing_file"),
				success: t("files.successfully_processed_file"),
			},
		);
	}
	return {
		dropzone,
		onOpen: dropzone.open,
		columnFormController: columnFormController as AsyncModalController<ImportColumnsFormSchema>,
		columnFormMethods,
		importErrorController: importErrorController as AsyncModalController<boolean>,
	};
}

/** Props Interface */
interface ColumnFormProps {
	methods: UseFormReturn<ImportColumnsFormSchema>;
}

export function ColumnForm({ methods }: ColumnFormProps) {
	/** Hooks */
	const { t } = useTranslation();

	const columNames = useWatch({ control: methods.control, name: "columnNames" });

	/** Render */
	return (
		<div className="v-stack gap-4">
			<Select
				label={t("general.quantity")}
				isRequired
				isNullable={false}
				control={methods.control}
				name="quantityColumnName"
				placeholder={t("general.select")}
				options={guardArray(columNames).map((column) => ({
					label: column,
					value: column,
					item: { column },
				}))}
			/>
			<Select
				label={t("general.identifier")}
				isRequired
				isNullable={false}
				control={methods.control}
				name="identifierColumnName"
				placeholder={t("general.select")}
				options={guardArray(columNames).map((column) => ({
					label: column,
					value: column,
					item: { column },
				}))}
			/>
		</div>
	);
}
